import React, { AnchorHTMLAttributes } from "react";
import { LoadingButton, LoadingButtonProps } from "@mui/lab";
import { ButtonGroup, styled, Tooltip, Theme, TooltipProps } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";

const groupBorder = (theme: Theme) =>
  theme.palette.mode === "light"
    ? lighten(theme.palette.highlighted, 0x72 / 0xff)
    : darken(theme.palette.highlighted, 0x72 / 0xff);
export type ZigButtonProps = Omit<LoadingButtonProps, "size" | "variant"> & {
  size?: LoadingButtonProps["size"] | "xlarge";
  variant?: LoadingButtonProps["variant"];
  ctaId?: string;
  tooltip?: string;
  narrow?: boolean;
  tooltipProps?: Partial<TooltipProps>;
  active?: boolean;
} & AnchorHTMLAttributes<HTMLAnchorElement>;

const ZigButton = ({
  active,
  tooltip,
  ctaId,
  color,
  size,
  narrow,
  variant = "contained",
  tooltipProps,
  target,
  rel,
  ...props
}: ZigButtonProps) => {
  const button = (
    <LoadingButton
      data-tack-cta={ctaId}
      // it wasn't me lol
      size={size as LoadingButtonProps["size"]}
      variant={variant as LoadingButtonProps["variant"]}
      {...props}
      {...(narrow
        ? {
            sx: {
              ...props.sx,
              minWidth: "0 !important",
              padding: "6px",
            },
          }
        : {})}
      {...(props.href
        ? {
            rel: rel ?? "noopener noreferrer",
            target: target ?? "_blank",
          }
        : {})}
      // @ts-ignore
      data-testid={props["data-testid"] || props.id || undefined}
      // hack to preserve old behavior but allow for normal mui theming
      color={variant === "outlined" && !color ? "secondary" : color}
      className={active ? `${props.className} MuiButton-active` : props.className}
    />
  );
  return tooltip ? (
    <Tooltip title={tooltip} {...(tooltipProps || {})}>
      {/* if we want to have a tooltip and have the button be disabled, we need a new container */}
      {props.disabled ? <span>{button}</span> : button}
    </Tooltip>
  ) : (
    button
  );
};

export const ZigButtonGroup = styled(ButtonGroup)`
  outline: 1px solid ${({ theme }) => theme.palette.neutral600};
  outline-offset: -1px;

  .MuiButton-root {
    font-weight: 400;
    text-transform: capitalize !important;

    &:hover,
    &.MuiButton-active {
      z-index: 3;
      border-color: ${({ theme }) => groupBorder(theme)} !important;
      border-radius: 5px;
      background: ${({ theme }) => theme.palette.neutral750};
      color: ${({ theme }) => theme.palette.highlighted};

      + button,
      + span button {
        border-left-color: transparent;
      }
    }

    &:not(:last-child) {
      border-right-color: transparent;
    }

    &.Mui-disabled {
      border-color: ${({ theme }) => theme.palette.neutral600};
      color: ${({ theme }) => theme.palette.contrasting}33;
    }
  }

  span button {
    height: 100%;
  }
` as typeof ButtonGroup;

export default ZigButton as typeof LoadingButton & typeof ZigButton;
